/**
 * Which days a medicine is taken — docs/33 C3.
 *
 * The doctor's shorthand says how many times a day; it cannot say "only on
 * Mondays" or "every other day". These are the patterns offered as plain
 * sentences when adding a medicine, each mapped onto what the schedule
 * engine already understands: a list of weekdays, or an on/off cycle that
 * starts on the first day. Read back before saving, like the shorthand.
 *
 * A calendar month ("on the 1st") is not offered: the engine has no month,
 * and "every 30 days" would drift from the date the doctor meant.
 */
import type { LocalDate, Weekday } from './types.js';

export type DayPattern =
  | { kind: 'every_day' }
  | { kind: 'weekdays'; days: Weekday[] }
  | { kind: 'every_other_day' }
  | { kind: 'every_n_days'; n: number }
  | { kind: 'on_off'; on: number; off: number };

export const DAY_PATTERN_KINDS = ['every_day', 'weekdays', 'every_other_day', 'every_n_days', 'on_off'] as const;

export interface DayFields {
  daysOfWeek: Weekday[] | null;
  cycle: { onDays: number; offDays: number; startDate: LocalDate } | null;
}

/** The schedule fields for a pattern, its cycle starting on `start`. */
export function dayFields(p: DayPattern, start: LocalDate): DayFields {
  switch (p.kind) {
    case 'every_day':
      return { daysOfWeek: null, cycle: null };
    case 'weekdays':
      return { daysOfWeek: p.days.length > 0 && p.days.length < 7 ? [...new Set(p.days)].sort() as Weekday[] : null, cycle: null };
    case 'every_other_day':
      return { daysOfWeek: null, cycle: { onDays: 1, offDays: 1, startDate: start } };
    case 'every_n_days':
      return { daysOfWeek: null, cycle: { onDays: 1, offDays: Math.max(1, Math.round(p.n) - 1), startDate: start } };
    case 'on_off':
      return { daysOfWeek: null, cycle: { onDays: Math.max(1, Math.round(p.on)), offDays: Math.max(0, Math.round(p.off)), startDate: start } };
  }
}

/** Nothing chosen yet that could be saved (no weekday ticked). */
export function dayPatternIncomplete(p: DayPattern): boolean {
  return p.kind === 'weekdays' && p.days.length === 0;
}
